import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { useUpdateShipment } from '@modules/Invoices/InvoicesOrders/List/hooks/useUpdateShipment';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal/ConfirmModal';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { useBlockShipment } from '../../hooks/useBlockShipment';
import { useBulkInvoiceViewPreInvoices } from '../../hooks/useBulkInvoiceViewPreInvoices';
import { useCreatePreInvoice } from '../../hooks/useCreatePreInvoice';
import { useInvoicePreInvoice } from '../../hooks/useInvoicePreInvoice';
import { usePreInvoice } from '../../hooks/usePreInvoice';
import { useRemoveStorageFromPreInvoice } from '../../hooks/useRemoveStorageFromPreInvoice';
import { useReturnShipment } from '../../hooks/useReturnShipment';
import { useViewPreInvoicesMain } from '../../hooks/useViewPreInvoicesMain';
import { PreInvoiceHeader, ViewPreInvoicesAgGridTable, ViewPreInvoiceStoragesAgGridTable } from '../display';
import { Breadcrumb } from '../display/Breadcrumb';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection } from '../interaction';
import type { BlockShipmentModalRef } from '../modals';
import { BlockShipmentModal } from '../modals';
// Importar modales de edición desde InvoicesOrders
import { EditarMasivoModal, EditarPrefacturaModal } from '@modules/Invoices/InvoicesOrders/List/components/modals';
import type { EditarMasivoModalRef } from '@modules/Invoices/InvoicesOrders/List/components/modals/EditarMasivoModal';
import type { EditarPrefacturaModalRef } from '@modules/Invoices/InvoicesOrders/List/components/modals/EditarPrefacturaModal';

export const Main = () => {
  const { t } = useTranslation('viewPreInvoicesModule');
  // const navigate = useNavigate(); // reserved for future navigation
  const confirmInvoiceModalRef = useRef<ConfirmModalRef>(null);
  const confirmBulkInvoiceModalRef = useRef<ConfirmModalRef>(null);
  const confirmCreatePreInvoiceModalRef = useRef<ConfirmModalRef>(null);
  const confirmReturnShipmentModalRef = useRef<ConfirmModalRef>(null);
  const blockShipmentModalRef = useRef<BlockShipmentModalRef>(null);
  const editarPrefacturaModalRef = useRef<EditarPrefacturaModalRef>(null);
  const editarMasivoModalRef = useRef<EditarMasivoModalRef>(null);

  const { preInvoice, isFetching: isFetchingPreInvoice } = usePreInvoice();
  useInvoicePreInvoice(); // invoicePreInvoice and isInvoicing are reserved for future use
  const { bulkInvoiceViewPreInvoices } = useBulkInvoiceViewPreInvoices();
  const { createPreInvoice } = useCreatePreInvoice();
  const { blockShipment } = useBlockShipment();
  const { returnShipment } = useReturnShipment();
  const { updateShipment } = useUpdateShipment();
  const { removeStorageFromPreInvoice } = useRemoveStorageFromPreInvoice();
  const currency = useCurrency();

  // Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Hook que maneja toda la lógica de negocio de prefacturas
  const {
    // Estados
    selectedViewPreInvoices,
    selectedColumns,

    // Datos
    shipments,
    storageTableRows,
    isStorageType,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    viewPreInvoicesViewProps,
    showActions,

    // Handlers
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleClearSelection,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Handlers de selección
    handleSelectionChanged,

    // Estado de filtros
    hasActiveFilters,
    filterConditions,
    searchTerm,
    // filterOptions, // reserved for future use
    permissions,
  } = useViewPreInvoicesMain();

  const { canEditLines, canRestoreLines, canBlockLines } = permissions;
  const showRowActions = showActions;

  // 🎯 Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    // Si isLoading pasa de true a false, significa que la carga inicial terminó.
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // 🎯 Helper para formatear precios con moneda
  const formatPrice = (value: number | undefined): string => {
    if (value === undefined || value === null) return `0,00 ${currency}`;
    return (
      new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value) +
      ' ' +
      currency
    );
  };

  // 🎯 Handler para editar shipment individual
  const handleEditSingleShipment = useCallback(
    (shipmentId: number) => {
      if (!canEditLines) return;

      // Buscar el shipment en los datos
      const shipment = shipments.find((s: any) => s.id === shipmentId);
      if (!shipment) return;

      const order = (shipment as any).order;

      // Obtener líneas del shipment
      const lines = ((shipment as any).lines || []).map((line: any) => ({
        id: line.id,
        concept: line.concept,
        base_amount: line.base_amount || 0,
      }));

      const shippingLine = lines.find((l: any) => l.concept === 'SHIPPING');
      const preparationLine = lines.find((l: any) => l.concept === 'PREPARATION');
      const codLine = lines.find((l: any) => l.concept === 'COD');

      // Determinar tipo de envío
      let shipmentType = 'Envío';
      if ((shipment as any).type === 'RETURN') {
        shipmentType = 'Devolución';
      } else if ((shipment as any).type === 'RESEND') {
        shipmentType = 'Reenvío';
      }

      // Determinar origen/destino usando los campos correctos de la API
      const customerAddress = order?.customer?.address;
      let originCountry = '',
        originMunicipality = '',
        originPostalCode = '';
      let destinationCountry = '',
        destinationMunicipality = '',
        destinationPostalCode = '';

      if ((shipment as any).type === 'RETURN' && customerAddress) {
        // Devolución: origen es el cliente, destino es Beeping
        originCountry = customerAddress.shipping_country || customerAddress.shipping_country_code || '';
        originMunicipality = customerAddress.shipping_city || '';
        originPostalCode = customerAddress.shipping_zip || '';
        destinationCountry = 'Beeping';
        destinationMunicipality = '';
        destinationPostalCode = '';
      } else {
        // Envío normal: origen es Beeping, destino es el cliente
        originCountry = 'Beeping';
        originMunicipality = '';
        originPostalCode = '';
        if (customerAddress) {
          destinationCountry = customerAddress.shipping_country || customerAddress.shipping_country_code || '';
          destinationMunicipality = customerAddress.shipping_city || '';
          destinationPostalCode = customerAddress.shipping_zip || '';
        }
      }

      // Calcular total de todas las líneas (base_amount)
      const total = lines.reduce((sum: number, line: any) => sum + (line.base_amount || 0), 0);

      editarPrefacturaModalRef.current?.open({
        reference: order?.ref || '',
        shop: order?.shop?.name || '',
        client: order?.team?.name || '', // El cliente es el team
        paymentMethod: order?.payment_method_method || '',
        internalId: (shipment as any).order_id?.toString() || '',
        shippingPrice: formatPrice(shippingLine?.base_amount),
        preparationPrice: formatPrice(preparationLine?.base_amount),
        codPrice: formatPrice(codLine?.base_amount),
        totalPrice: formatPrice(total),
        type: (shipment as any).type || '',
        logisticStatus: order?.shipment_status?.name || '',
        shippingOrReturn: shipmentType,
        courier: (shipment as any).courier?.name || order?.shipment?.courier?.name || '',
        orderWeight: formatPrice((shipment as any).weight_internal_kg),
        numberOfLines: ((shipment as any).num_items || 0).toString(),
        originCountry,
        originMunicipality,
        originPostalCode,
        destinationCountry,
        destinationMunicipality,
        destinationPostalCode,
        onSave: async (data) => {
          // Extraer valores numéricos de los precios formateados
          const extractNumber = (value: string): number => {
            const cleaned = value.replace(/[^\d,.-]/g, '').replace(',', '.');
            return parseFloat(cleaned) || 0;
          };

          const shippingAmount = extractNumber(data.shippingPrice);
          const preparationAmount = extractNumber(data.preparationPrice);
          const codAmount = extractNumber(data.codPrice);
          const weightKg = extractNumber(data.orderWeight);
          const numItems = parseInt(data.numberOfLines, 10) || 0;

          // Construir payload para la API
          const linesPayload = lines.map((line: { id: number; concept: string; base_amount: number }) => {
            let newBaseAmount = line.base_amount;
            if (line.concept === 'SHIPPING') {
              newBaseAmount = shippingAmount;
            } else if (line.concept === 'PREPARATION') {
              newBaseAmount = preparationAmount;
            } else if (line.concept === 'COD') {
              newBaseAmount = codAmount;
            }
            return {
              id: line.id,
              base_amount: newBaseAmount,
            };
          });

          const payload = {
            weight_internal_kg: weightKg,
            num_items: numItems,
            lines: linesPayload,
          };

          await updateShipment(shipment.id, payload, () => {
            refetch();
          });
        },
      });
    },
    [canEditLines, shipments, refetch, updateShipment, currency],
  );

  // 🎯 Handler para editar múltiples shipments
  const handleBulkEditShipments = useCallback(() => {
    const selectedIds = Array.from(selectedViewPreInvoices);

    if (selectedIds.length === 0) return;

    const shipmentsForEdit = shipments
      .filter((s: any) => selectedIds.includes(s.id))
      .map((s: any) => ({
        id: s.id,
        reference: s.order?.ref || '',
        shop: s.order?.shop?.name || '',
        client: s.order?.team?.name || '',
        lines: (s.lines || []).map((l: any) => ({
          id: l.id,
          concept: l.concept,
          base_amount: l.base_amount || 0,
        })),
        weight_internal_kg: s.weight_internal_kg || 0,
        num_items: s.num_items || 0,
      }));

    editarMasivoModalRef.current?.open({
      shipments: shipmentsForEdit,
      onSuccess: () => {
        handleClearSelection();
        refetch();
      },
    });
  }, [selectedViewPreInvoices, shipments, handleClearSelection, refetch]);

  // 🎯 Handler para remover storage de prefactura
  const handleRemoveStorage = useCallback(
    (storageId: number) => {
      confirmReturnShipmentModalRef.current?.open(
        () => {
          removeStorageFromPreInvoice(storageId, undefined, () => {
            refetch();
          });
        },
        undefined,
        t('confirmModals.removeStorage.title', 'Remover storage'),
        t('confirmModals.removeStorage.message', '¿Estás seguro de que deseas remover este storage de la prefactura?'),
        t('confirmModals.removeStorage.confirm', 'Remover'),
        t('confirmModals.removeStorage.cancel', 'Cancelar'),
      );
    },
    [removeStorageFromPreInvoice, refetch, t],
  );

  // 🎯 Handler para devolver prefactura a pedidos
  const handleReturnPreInvoice = useCallback(
    (shipmentId: number) => {
      if (!canRestoreLines) {
        return;
      }
      if (!preInvoice?.id) {
        console.log('🔍 Main - No se encontró el ID de prefactura para devolver el shipment');
        return;
      }

      confirmReturnShipmentModalRef.current?.open(
        () => {
          returnShipment({
            preInvoiceId: preInvoice.id,
            shipmentId,
            onSuccess: () => {
              refetch();
            },
          });
        },
        undefined,
        t('confirmModals.returnShipment.title', 'Devolver a pedidos'),
        t('confirmModals.returnShipment.message', {
          shipmentId,
          defaultValue: '¿Estás seguro de que deseas devolver este shipment a pedidos?',
        }),
        t('confirmModals.returnShipment.confirm', 'Devolver'),
        t('confirmModals.returnShipment.cancel', 'Cancelar'),
      );
    },
    [canRestoreLines, preInvoice, returnShipment, refetch, t],
  );

  // 🎯 Handler para bloquear shipment
  const handleBlockPreInvoice = useCallback(
    (shipmentId: number) => {
      if (!canBlockLines) {
        return;
      }
      // Mostrar modal de bloqueo con formulario de motivo y comentario
      blockShipmentModalRef.current?.open(shipmentId, (data) => {
        // Usuario confirmó: ejecutar bloqueo con los datos del formulario
        blockShipment(shipmentId, data, () => {
          // Refetch de datos después del bloqueo exitoso
          refetch();
        });
      });
    },
    [canBlockLines, blockShipment, refetch],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleBulkInvoiceViewPreInvoices = useCallback(() => {
    // Obtener los IDs de las prefacturas seleccionadas
    const selectedIds = Array.from(selectedViewPreInvoices);

    if (selectedIds.length === 0) {
      return;
    }

    // Mostrar confirmación antes de facturar
    confirmBulkInvoiceModalRef.current?.open(
      () => {
        // Usuario confirmó: ejecutar facturación masiva
        bulkInvoiceViewPreInvoices(selectedIds, () => {
          // Limpiar selección y refetch de datos después de la facturación exitosa
          handleClearSelection();
          refetch();
        });
      },
      undefined,
      t('confirmModals.bulkInvoicePreInvoice.title', 'Facturar prefacturas'),
      t('confirmModals.bulkInvoicePreInvoice.message', {
        count: selectedIds.length,
        defaultValue: `¿Estás seguro de que deseas facturar ${selectedIds.length} prefacturas?`,
      }),
      t('confirmModals.bulkInvoicePreInvoice.confirm', 'Facturar'),
      t('confirmModals.bulkInvoicePreInvoice.cancel', 'Cancelar'),
    );
  }, [selectedViewPreInvoices, bulkInvoiceViewPreInvoices, handleClearSelection, refetch, t]);

  // 🎯 Handler para crear prefactura desde shipments seleccionados (SelectionToolbar)
  const handleCreatePreInvoiceFromSelection = useCallback(() => {
    // Obtener los IDs de shipments seleccionados
    const selectedIds = Array.from(selectedViewPreInvoices);

    if (selectedIds.length === 0) {
      return;
    }

    // Mostrar confirmación antes de crear prefactura
    confirmCreatePreInvoiceModalRef.current?.open(
      () => {
        // Usuario confirmó: crear prefactura con los shipments seleccionados
        createPreInvoice(selectedIds, () => {
          // Limpiar selección y refetch de datos después de crear exitosamente
          handleClearSelection();
          refetch();
        });
      },
      undefined,
      t('confirmModals.createPreInvoice.title', 'Crear prefactura'),
      t('confirmModals.createPreInvoice.message', {
        count: selectedIds.length,
        defaultValue: `¿Estás seguro de que deseas crear una prefactura con ${selectedIds.length} envíos?`,
      }),
      t('confirmModals.createPreInvoice.confirm', 'Crear prefactura'),
      t('confirmModals.createPreInvoice.cancel', 'Cancelar'),
    );
  }, [selectedViewPreInvoices, createPreInvoice, handleClearSelection, refetch, t]);

  // 🎯 Obtener columnas para el ColumnSelector usando el hook de AG Grid
  const { tanStackColumns } = useAgGridColumns({
    isStorageType,
    selectedColumns,
    onEditPreInvoice: handleEditSingleShipment,
    onReturnPreInvoice: handleReturnPreInvoice,
    onBlockPreInvoice: handleBlockPreInvoice,
  });

  // Columnas para el selector: usar las columnas de AG Grid convertidas a TanStack
  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return (
      <>
        <Breadcrumb preInvoiceCode={preInvoice?.code} />
        <div className="px-6 pt-4 flex flex-col flex-1 min-h-0">
          <LoadingState message={t('loading.message', 'Cargando prefacturas...')} className="flex-1 min-h-0" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Breadcrumb preInvoiceCode={preInvoice?.code} />
        <div className="px-6 pt-4 flex flex-col flex-1 min-h-0">
          <ErrorState error={error} onRetry={refetch} className="flex-1 min-h-0" />
        </div>
      </>
    );
  }

  return (
    <div className="px-6 pt-4 pb-4 flex flex-col flex-1 min-h-0">
      <div className="shrink-0 space-y-4">
        <Breadcrumb preInvoiceCode={preInvoice?.code} />

        {/* Header con información de la prefactura */}
        {preInvoice && <PreInvoiceHeader preInvoice={preInvoice} isFetching={isFetchingPreInvoice} />}

        {/* Header con filtros */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              columns={selectorColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              filterConditions={filterConditions}
              hasTeamData={false}
              searchTerm={searchTerm}
            />
          </div>
          <TotalResults total={total} />
        </div>
      </div>

      {/* Estado de carga */}
      {isLoading && (
        <div className="flex-1 min-h-0 flex items-stretch mt-4">
          <LoadingState message={t('loading.message')} className="flex-1 min-h-0" />
        </div>
      )}

      {/* Estado vacío */}
      {!isLoading && (isStorageType ? storageTableRows.length === 0 : shipments.length === 0) && (
        <div className="flex-1 min-h-0 flex flex-col justify-center mt-4">
          <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.ORDERS} />
        </div>
      )}

      {/* Tabla de shipments */}
      {!isLoading && !isStorageType && shipments.length > 0 && (
        <div
          className={`flex flex-col flex-1 min-h-0 mt-4 transition-opacity duration-300 ${
            isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
          }`}
        >
          <ViewPreInvoicesAgGridTable
            {...viewPreInvoicesViewProps}
            rows={shipments}
            selectedViewPreInvoices={selectedViewPreInvoices}
            selectedColumns={selectedColumns}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onSelectionChanged={handleSelectionChanged}
            onEditPreInvoice={handleEditSingleShipment}
            onReturnPreInvoice={handleReturnPreInvoice}
            onBlockPreInvoice={handleBlockPreInvoice}
            onBulkInvoiceViewPreInvoices={handleCreatePreInvoiceFromSelection}
            onBulkEditShipments={handleBulkEditShipments}
            showActions={showRowActions}
          />
        </div>
      )}

      {/* Tabla de storages */}
      {!isLoading && isStorageType && storageTableRows.length > 0 && (
        <div
          className={`flex flex-col flex-1 min-h-0 mt-4 transition-opacity duration-300 ${
            isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
          }`}
        >
          <ViewPreInvoiceStoragesAgGridTable
            {...viewPreInvoicesViewProps}
            rows={storageTableRows}
            selectedStorages={new Set(Array.from(selectedViewPreInvoices).map((id) => `${id}`))}
            selectedColumns={selectedColumns}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onSelectionChanged={handleSelectionChanged}
            onRemoveStorage={handleRemoveStorage}
            showActions={true}
          />
        </div>
      )}

      {/* Modal de confirmación para facturar prefactura individual */}
      <ConfirmModal ref={confirmInvoiceModalRef} />

      {/* Modal de confirmación para facturar prefacturas en bloque */}
      <ConfirmModal ref={confirmBulkInvoiceModalRef} />

      {/* Modal de confirmación para crear prefactura desde shipments */}
      <ConfirmModal ref={confirmCreatePreInvoiceModalRef} />
      <ConfirmModal ref={confirmReturnShipmentModalRef} />

      {/* Modal para bloquear shipment */}
      <BlockShipmentModal ref={blockShipmentModalRef} />

      {/* Modal para editar shipment individual */}
      <EditarPrefacturaModal ref={editarPrefacturaModalRef} />

      {/* Modal para edición masiva de shipments */}
      <EditarMasivoModal ref={editarMasivoModalRef} />
    </div>
  );
};
